import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { durableStorage, persistAfterRecovery } from '@/lib/durableStorage';
import type {
  MatchEventDto,
  PendingEvent,
  PendingUndo,
  RefusedEvent,
  SyncStatus,
  MatchState,
} from '@/common/model';

export interface SyncStore {
  matchId: string | null;
  status: SyncStatus;
  pendingEvents: PendingEvent[];
  /** Sent before the actions of their match, so a resent action follows its undo. */
  pendingUndos: PendingUndo[];
  /** Actions the server did not record, until the coach has checked them. */
  refusedEvents: RefusedEvent[];
  /**
   * Matches whose queue a refusal holds on the device: `readOnly` (team locked,
   * past season, access removed) lifts once access is back; `refused` (refused
   * for good) never lifts on its own. Persisted: a reload must not promise
   * these actions will leave by themselves.
   */
  heldMatches: Record<string, HeldReason>;
  /**
   * Refused actions already named to the coach. Persisted: a kept action is
   * refused again at every retry, and after a reload it must not come back
   * once the coach chose « Checked, hide » (VOL-208).
   */
  notifiedRefusedIds: string[];
  /** Opponent of each match with queued actions, to name it outside its live screen. */
  matchOpponents: Record<string, string>;
  /** Name of each account with queued actions, to name « another account ». */
  userNames: Record<string, string>;
  lastSyncedSequence: number;
  lastServerState: MatchState | null;
  lastError: string | null;
  /** The last error clears up by itself: the indicator shows a retry, not an error (VOL-324). */
  errorRetrying: boolean;

  setMatchId: (id: string | null) => void;
  addPendingEvent: (event: MatchEventDto) => void;
  removePendingEvents: (eventIds: string[]) => void;
  addPendingUndo: (eventId: string, matchId: string, label?: string | null) => void;
  markRefusedNotified: (eventIds: string[]) => void;
  /** Remembers who queued for which opponent, for warnings shown away from the match. */
  rememberQueueContext: (matchId: string | null, opponent: string | null) => void;
  /**
   * A coach signed in on the device takes over the queue another account left
   * (a member removed mid-match is never let back in): its actions leave with
   * this account, their author kept in `enteredByUserId` (VOL-198). Only that
   * match when given. Returns how many actions and undos changed hands.
   */
  adoptPending: (fromUserId: string, matchId?: string) => number;
  removePendingUndos: (eventIds: string[]) => void;
  updateUndoRetryCount: (eventId: string) => void;
  removePendingForMatch: (matchId: string) => void;
  addRefusedEvents: (refused: RefusedEvent[]) => void;
  /** Kept actions that finally went through are no longer refused. */
  removeRefusedEvents: (eventIds: string[]) => void;
  dismissRefusedEvents: (matchId: string, userId: string | null) => void;
  setMatchHeld: (matchId: string, reason: HeldReason | null) => void;
  updateRetryCount: (eventId: string) => void;
  /** Dates the first server error (5xx) of these actions; an earlier date is kept. */
  markServerError: (eventIds: string[], at: number) => void;
  clearServerError: (eventIds: string[]) => void;
  /** Flags actions about to be sent: from then on the server may hold them. */
  markAttempted: (eventIds: string[]) => void;
  setStatus: (status: SyncStatus) => void;
  setLastSequence: (seq: number) => void;
  setServerState: (state: MatchState | null) => void;
  /** `retrying`: the error clears up by itself (timeout, unreachable, 5xx), the send is retried. */
  setError: (error: string | null, options?: { retrying?: boolean }) => void;
  clearPendingForMatch: () => void;
  reset: () => void;
}

export type HeldReason = 'readOnly' | 'refused';

type PersistedSyncState = Pick<
  SyncStore,
  | 'matchId'
  | 'pendingEvents'
  | 'pendingUndos'
  | 'refusedEvents'
  | 'heldMatches'
  | 'notifiedRefusedIds'
  | 'matchOpponents'
  | 'userNames'
  | 'lastSyncedSequence'
>;

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record;
  const { [key]: _removed, ...rest } = record;
  return rest;
}

/** Enough to name what was refused; older ones were long checked or forgotten. */
const MAX_REFUSED_EVENTS = 50;
/** Retries refuse at most a few batches again: older ids are long gone from the queue. */
const MAX_NOTIFIED_REFUSED = 200;
/** A few matches and accounts at most share a tablet. */
const MAX_QUEUE_LABELS = 20;

/** Sets `key` last, so the oldest entries are the ones dropped past the bound. */
function withLabel(record: Record<string, string>, key: string, value: string): Record<string, string> {
  if (record[key] === value) return record;
  const entries = Object.entries(withoutKey(record, key));
  return Object.fromEntries([...entries, [key, value]].slice(-MAX_QUEUE_LABELS));
}

// Registered by the auth controller: importing it here would pull the whole
// auth graph (analytics, query client) into this low-level store.
let currentUserIdProvider: () => string | null = () => null;
let currentUserNameProvider: () => string | null = () => null;
let canRelayProvider: () => boolean = () => false;

export function setSyncUserProvider(
  provider: () => string | null,
  nameProvider: () => string | null = () => null,
  relayProvider: () => boolean = () => false,
): void {
  currentUserIdProvider = provider;
  currentUserNameProvider = nameProvider;
  canRelayProvider = relayProvider;
}

/** Whether the signed-in user may send another account's queued actions (a coach, VOL-198). */
export function canRelayPending(): boolean {
  return currentUserId() !== null && canRelayProvider();
}

function currentUserId(): string | null {
  return currentUserIdProvider();
}

/**
 * Whether the signed-in user may send this action: another user's actions stay
 * on the device until that user signs in again, never sent with this account.
 */
export function isOwnPending(pe: Pick<PendingEvent | PendingUndo, 'userId'>): boolean {
  return isOwnedBy(pe, currentUserId());
}

/** `isOwnPending` against a given user: screens pass the one they render for, reactively. */
export function isOwnedBy(item: { userId: string | null }, userId: string | null): boolean {
  return item.userId == null || item.userId === userId;
}

/** Queued actions of the match on screen; the queue also holds other matches'. */
export function currentMatchPending(
  state: Pick<SyncStore, 'matchId' | 'pendingEvents'>,
): PendingEvent[] {
  return state.pendingEvents.filter((pe) => pe.matchId === state.matchId && isOwnPending(pe));
}

/**
 * What the match on screen still has to send: actions and undos. Anything
 * waiting means the server is behind the tablet, so loading its state would
 * bring back an undone action.
 */
export function currentMatchPendingCount(
  state: Pick<SyncStore, 'matchId' | 'pendingEvents' | 'pendingUndos'>,
): number {
  const undos = state.pendingUndos.filter((pu) => pu.matchId === state.matchId && isOwnPending(pu));
  return currentMatchPending(state).length + undos.length;
}

export const selectCurrentMatchPendingCount = (state: SyncStore): number =>
  currentMatchPendingCount(state);

/**
 * Actions and undos this account still has to send, all matches together.
 * Actions tied to no server match are left out: they are never sent.
 */
export function ownQueuedCount(
  state: Pick<SyncStore, 'pendingEvents' | 'pendingUndos'>,
  userId: string | null,
): number {
  const events = state.pendingEvents.filter((pe) => pe.matchId !== null && isOwnedBy(pe, userId));
  const undos = state.pendingUndos.filter((pu) => isOwnedBy(pu, userId));
  return events.length + undos.length;
}

/**
 * Version 1: queued actions before it carried no match and were all sent to
 * the persisted current match, give them that one. Version 2: undo queue.
 * Version 3: refused actions. Version 4: refusals already named, opponents and
 * account names of the queue.
 */
export function migrateSyncStorage(persisted: unknown, version: number): PersistedSyncState {
  let state = persisted as PersistedSyncState;
  if (version < 1) {
    const matchId = state.matchId ?? null;
    state = {
      ...state,
      pendingEvents: (state.pendingEvents ?? []).map((pe) => ({
        ...pe,
        matchId: pe.matchId ?? matchId,
        userId: pe.userId ?? null,
      })),
    };
  }
  if (version < 2) state = { ...state, pendingUndos: [] };
  if (version < 3) state = { ...state, refusedEvents: [], heldMatches: {} };
  if (version < 4) {
    state = {
      ...state,
      // Refusals kept before were named in this page load only: treat them as named,
      // the history panel still lists them.
      notifiedRefusedIds: (state.refusedEvents ?? []).map((r) => r.event.eventId),
      matchOpponents: {},
      userNames: {},
    };
  }
  return state;
}

/**
 * Restored state over the current one, as zustand does, but keeping actions
 * queued while it loaded: a state read back from IndexedDB arrives after
 * start-up, and replacing the queue would drop them (VOL-14).
 */
export function mergeSyncState(persisted: unknown, current: SyncStore): SyncStore {
  if (!persisted || typeof persisted !== 'object') return current;
  const restored = persisted as Partial<PersistedSyncState>;
  const restoredEvents = restored.pendingEvents ?? [];
  const restoredUndos = restored.pendingUndos ?? [];
  const knownEvents = new Set(restoredEvents.map((pe) => pe.event.eventId));
  const knownUndos = new Set(restoredUndos.map((pu) => `${pu.matchId}:${pu.eventId}`));
  return {
    ...current,
    ...restored,
    pendingEvents: [
      ...restoredEvents,
      ...current.pendingEvents.filter((pe) => !knownEvents.has(pe.event.eventId)),
    ],
    pendingUndos: [
      ...restoredUndos,
      ...current.pendingUndos.filter((pu) => !knownUndos.has(`${pu.matchId}:${pu.eventId}`)),
    ],
  };
}

export const useSyncStore = create<SyncStore>()(
  persist(
    (set, get) => ({
      matchId: null,
      status: 'idle',
      pendingEvents: [],
      pendingUndos: [],
      refusedEvents: [],
      heldMatches: {},
      notifiedRefusedIds: [],
      matchOpponents: {},
      userNames: {},
      lastSyncedSequence: 0,
      lastServerState: null,
      lastError: null,
      errorRetrying: false,

      // Actions entered before the server created the match join it; actions
      // already tied to another match keep going there (VOL-273).
      setMatchId: (id) =>
        set((state) => ({
          matchId: id,
          pendingEvents: id === null
            ? state.pendingEvents
            : state.pendingEvents.map((pe) => (pe.matchId === null ? { ...pe, matchId: id } : pe)),
        })),

      addPendingEvent: (event) =>
        set((state) => ({
          pendingEvents: [
            ...state.pendingEvents,
            {
              event,
              matchId: state.matchId,
              userId: currentUserId(),
              retryCount: 0,
              createdAt: Date.now(),
            },
          ],
        })),

      removePendingEvents: (eventIds) =>
        set((state) => ({
          pendingEvents: state.pendingEvents.filter(
            (pe) => !eventIds.includes(pe.event.eventId),
          ),
        })),

      addPendingUndo: (eventId, matchId, label) =>
        set((state) => (
          state.pendingUndos.some((pu) => pu.eventId === eventId && pu.matchId === matchId)
            ? state
            : {
              pendingUndos: [
                ...state.pendingUndos,
                { eventId, matchId, userId: currentUserId(), retryCount: 0, createdAt: Date.now(), label: label ?? null },
              ],
            }
        )),

      markRefusedNotified: (eventIds) =>
        set((state) => {
          const known = new Set(state.notifiedRefusedIds);
          const added = eventIds.filter((id) => !known.has(id));
          if (added.length === 0) return state;
          return { notifiedRefusedIds: [...state.notifiedRefusedIds, ...added].slice(-MAX_NOTIFIED_REFUSED) };
        }),

      rememberQueueContext: (matchId, opponent) =>
        set((state) => {
          const userId = currentUserId();
          const userName = currentUserNameProvider();
          const matchOpponents = matchId && opponent?.trim()
            ? withLabel(state.matchOpponents, matchId, opponent.trim())
            : state.matchOpponents;
          const userNames = userId && userName?.trim()
            ? withLabel(state.userNames, userId, userName.trim())
            : state.userNames;
          return matchOpponents === state.matchOpponents && userNames === state.userNames
            ? state
            : { matchOpponents, userNames };
        }),

      adoptPending: (fromUserId, matchId) => {
        const me = currentUserId();
        if (!me || me === fromUserId || !canRelayPending()) return 0;
        const taken = (item: { matchId: string | null; userId: string | null }) =>
          item.matchId !== null && item.userId === fromUserId && (matchId === undefined || item.matchId === matchId);
        const state = get();
        const events = state.pendingEvents.filter(taken);
        const undos = state.pendingUndos.filter(taken);
        if (events.length + undos.length === 0) return 0;
        const matches = new Set([...events, ...undos].map((item) => item.matchId as string));
        // The access refusal that held these matches was the other account's;
        // a refusal of the actions themselves stays until the server says otherwise.
        const heldMatches = Object.fromEntries(
          Object.entries(state.heldMatches).filter(([id, reason]) => !(matches.has(id) && reason === 'readOnly')),
        );
        set({
          pendingEvents: state.pendingEvents.map((pe) => (
            taken(pe)
              ? {
                ...pe,
                userId: me,
                // Attempts were used up by the other account's refusals, not by these actions.
                retryCount: 0,
                serverErrorSince: undefined,
                event: { ...pe.event, enteredByUserId: pe.event.enteredByUserId ?? fromUserId },
              }
              : pe
          )),
          pendingUndos: state.pendingUndos.map((pu) => (taken(pu) ? { ...pu, userId: me, retryCount: 0 } : pu)),
          refusedEvents: state.refusedEvents.map((r) => (
            r.userId === fromUserId && matches.has(r.matchId) ? { ...r, userId: me } : r
          )),
          heldMatches,
        });
        return events.length + undos.length;
      },

      removePendingUndos: (eventIds) =>
        set((state) => ({
          pendingUndos: state.pendingUndos.filter((pu) => !eventIds.includes(pu.eventId)),
        })),

      updateUndoRetryCount: (eventId) =>
        set((state) => ({
          pendingUndos: state.pendingUndos.map((pu) =>
            pu.eventId === eventId ? { ...pu, retryCount: pu.retryCount + 1 } : pu,
          ),
        })),

      removePendingForMatch: (matchId) =>
        set((state) => ({
          pendingEvents: state.pendingEvents.filter((pe) => pe.matchId !== matchId),
          pendingUndos: state.pendingUndos.filter((pu) => pu.matchId !== matchId),
          refusedEvents: state.refusedEvents.filter((r) => r.matchId !== matchId),
          heldMatches: withoutKey(state.heldMatches, matchId),
        })),

      addRefusedEvents: (refused) =>
        set((state) => {
          const incoming = new Map(refused.map((r) => [r.event.eventId, r]));
          // A kept action the server later acks `rejected` has left the queue:
          // its entry must say so, not « still on the tablet ».
          let changed = false;
          const updated = state.refusedEvents.map((r) => {
            const next = incoming.get(r.event.eventId);
            if (!next || next.status === r.status) return r;
            changed = true;
            return { ...r, status: next.status };
          });
          const known = new Set(state.refusedEvents.map((r) => r.event.eventId));
          const added = refused.filter((r) => !known.has(r.event.eventId));
          if (!changed && added.length === 0) return state;
          return { refusedEvents: [...updated, ...added].slice(-MAX_REFUSED_EVENTS) };
        }),

      removeRefusedEvents: (eventIds) =>
        set((state) => (
          state.refusedEvents.some((r) => eventIds.includes(r.event.eventId))
            ? { refusedEvents: state.refusedEvents.filter((r) => !eventIds.includes(r.event.eventId)) }
            : state
        )),

      // Only the coach's own: another account's refusals stay for that account.
      dismissRefusedEvents: (matchId, userId) =>
        set((state) => ({
          refusedEvents: state.refusedEvents.filter(
            (r) => r.matchId !== matchId || !isOwnedBy(r, userId),
          ),
        })),

      setMatchHeld: (matchId, reason) =>
        set((state) => {
          if ((state.heldMatches[matchId] ?? null) === reason) return state;
          return {
            heldMatches: reason === null
              ? withoutKey(state.heldMatches, matchId)
              : { ...state.heldMatches, [matchId]: reason },
          };
        }),

      updateRetryCount: (eventId) =>
        set((state) => ({
          pendingEvents: state.pendingEvents.map((pe) =>
            pe.event.eventId === eventId
              ? { ...pe, retryCount: pe.retryCount + 1 }
              : pe,
          ),
        })),

      markServerError: (eventIds, at) =>
        set((state) => ({
          pendingEvents: state.pendingEvents.map((pe) =>
            eventIds.includes(pe.event.eventId) && pe.serverErrorSince === undefined
              ? { ...pe, serverErrorSince: at }
              : pe,
          ),
        })),

      clearServerError: (eventIds) =>
        set((state) => ({
          pendingEvents: state.pendingEvents.map((pe) =>
            eventIds.includes(pe.event.eventId) && pe.serverErrorSince !== undefined
              ? { ...pe, serverErrorSince: undefined }
              : pe,
          ),
        })),

      markAttempted: (eventIds) =>
        set((state) => ({
          pendingEvents: state.pendingEvents.map((pe) =>
            eventIds.includes(pe.event.eventId) ? { ...pe, attempted: true } : pe,
          ),
        })),

      setStatus: (status) => set({ status }),
      setLastSequence: (seq) => set({ lastSyncedSequence: seq }),
      setServerState: (state) => set({ lastServerState: state }),
      setError: (error, options) =>
        set({ lastError: error, errorRetrying: error !== null && options?.retrying === true }),

      clearPendingForMatch: () =>
        set((state) => ({
          pendingEvents: state.pendingEvents.filter((pe) => pe.matchId !== state.matchId),
          pendingUndos: state.pendingUndos.filter((pu) => pu.matchId !== state.matchId),
        })),

      // Leaving a match must not lose its unsent actions: only those tied to no
      // server match (demo, match never created) are dropped.
      reset: () =>
        set((state) => ({
          matchId: null,
          status: 'idle',
          pendingEvents: state.pendingEvents.filter((pe) => pe.matchId !== null),
          lastSyncedSequence: 0,
          lastServerState: null,
          lastError: null,
      errorRetrying: false,
        })),
    }),
    {
      name: 'volley-sync-storage',
      storage: createJSONStorage(() => durableStorage),
      version: 4,
      migrate: migrateSyncStorage,
      merge: mergeSyncState,
      partialize: (state): PersistedSyncState => ({
        matchId: state.matchId,
        pendingEvents: state.pendingEvents,
        pendingUndos: state.pendingUndos,
        refusedEvents: state.refusedEvents,
        heldMatches: state.heldMatches,
        notifiedRefusedIds: state.notifiedRefusedIds,
        matchOpponents: state.matchOpponents,
        userNames: state.userNames,
        lastSyncedSequence: state.lastSyncedSequence,
      }),
    },
  ),
);

persistAfterRecovery(useSyncStore);
